import { Link } from "react-router-dom";
import { ArrowUpRight, ClipboardCheck, FileSearch, HelpCircle, Server } from "lucide-react";
import { useDocs } from "@/lib/DocsContext";
import { toTestId } from "@/lib/docs";
import { ApprovalBanner } from "@/components/docs/ApprovalBanner";
import { StatusChip, Tag } from "@/components/docs/StatusChip";

const STEPS = [
  { Icon: ClipboardCheck, title: "G0-R rev. 2 odobren", text: "Odobreni baseline je a9a736e. Pročitajte evidenciju zasebnih G0 i G1a odluka.", to: "/doc/review/G0-R-REV2-APPROVAL.md", tag: "Odobreno" },
  { Icon: HelpCircle, title: "G1a · sintetički Foundation", text: "Razvoj je zasebno dopušten. U-01 prije live upisa; U-02 samo create-only mock. G1b čeka pregled.", to: "/doc/plan/backlog.md", tag: "G1a odobren" },
  { Icon: Server, title: "Read-only audit VPS-a", text: "Tek prije G3: pregledajte runbook i podijelite maskirani rezultat, bez deploya sada.", to: "/doc/ops/runbook-vps-audit.md", tag: "Za G3" },
  { Icon: FileSearch, title: "Zoho/Zia provjere", text: "Zasebna proba po organizaciji tek za G4. U G0 se stvarni računi ne diraju.", to: "/doc/integrations/zoho-zia-capability-matrix.md", tag: "Za G4" },
];

const Fact = ({ label, value, i }) => (
  <div className="fade-up min-w-0 border-l border-border pl-4" style={{ animationDelay: `${120 + i * 50}ms` }} data-testid={`overview-fact-${i}`}>
    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
    <p className="mt-1 text-sm font-medium [overflow-wrap:anywhere]">{value}</p>
  </div>
);

const Step = ({ step, n }) => (
  <Link to={step.to} data-testid={`overview-step-${n}`} className="group relative flex flex-col rounded-lg border border-border bg-card p-5 transition-colors hover:border-primary/50">
    <div className="flex items-center justify-between">
      <span className="font-mono text-xs text-muted-foreground">0{n}</span>
      <Tag>{step.tag}</Tag>
    </div>
    <step.Icon className="mt-6 h-5 w-5 text-primary" />
    <h3 className="mt-3 font-heading text-base font-bold tracking-tight">{step.title}</h3>
    <p className="mt-1.5 text-sm text-muted-foreground">{step.text}</p>
    <ArrowUpRight className="absolute right-4 top-12 h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
  </Link>
);

const DocCard = ({ doc }) => (
  <Link
    to={`/doc/${doc.path}`}
    data-testid={`overview-doc-card-${toTestId(doc.path)}`}
    className="group flex flex-col justify-between gap-4 rounded-lg border border-border bg-card p-4 transition-[border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-primary/50"
  >
    <div>
      <div className="flex items-start justify-between gap-3">
        <h4 className="text-sm font-semibold leading-snug">{doc.title}</h4>
        <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
      </div>
      <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">{doc.summary}</p>
    </div>
    <div className="flex flex-wrap items-center gap-2">
      <StatusChip status={doc.status} />
      <Tag>{doc.type}</Tag>
    </div>
  </Link>
);

export default function OverviewPage() {
  const { manifest, docs, error } = useDocs();
  if (error) return <p className="p-10 text-danger" data-testid="overview-error">{error}</p>;
  if (!manifest) return <p className="p-10 text-muted-foreground" data-testid="overview-loading">Učitavanje paketa…</p>;

  const count = (s) => docs.filter((d) => d.status === s).length;

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-10" data-testid="overview-page">
      <ApprovalBanner />
      <section className="mt-14 grid gap-10 lg:grid-cols-[1.4fr_1fr]">
        <div className="fade-up">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-primary">MegaBooker AI Business OS · Foundation</p>
          <h1 className="mt-4 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="overview-title">
            G0-R rev. 2 · arhitekturni paket
          </h1>
          <p className="mt-5 max-w-xl text-base text-muted-foreground">
            ADR-ovi, model podataka, API ugovori, sigurnost i plan za VPS. G0-R rev. 2 je odobren na točnom commitu; G1a sintetički razvoj je dopušten zasebno, ali funkcionalnosti tek treba izgraditi i testirati. Bez live integracija ili G1b.
          </p>
          <div className="mt-6 flex flex-wrap gap-2 font-mono text-xs" data-testid="overview-counts">
            <span className="rounded border border-border px-2.5 py-1">{docs.length} dokumenata</span>
            <span className="rounded border border-ok/30 bg-ok/10 px-2.5 py-1 text-ok">{count("delivered")} dokumenata isporučeno (funkcionalnosti nisu izgrađene)</span>
            <span className="rounded border border-warn/30 bg-warn/10 px-2.5 py-1 text-warn">{count("action_required")} čeka vašu radnju</span>
            <span className="rounded border border-border px-2.5 py-1 text-muted-foreground">{count("deferred")} odgođeno</span>
          </div>
        </div>
        <div className="grid min-w-0 grid-cols-2 gap-x-6 gap-y-6 self-end">
          {manifest.facts.map((f, i) => <Fact key={f.label} {...f} i={i} />)}
        </div>
      </section>

      <section className="mt-20" data-testid="overview-steps">
        <h2 className="font-heading text-base font-bold tracking-tight md:text-lg">Što od vas trebam</h2>
        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => <Step key={s.title} step={s} n={i + 1} />)}
        </div>
      </section>

      <section className="mt-20 space-y-12" data-testid="overview-deliverables">
        {manifest.groups.map((g, i) => (
          <div key={g.id} data-testid={`overview-group-${g.id}`}>
            <div className="mb-4 flex items-baseline gap-3 border-b border-border pb-3">
              <span className="font-mono text-xs text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
              <h2 className="font-heading text-base font-bold tracking-tight md:text-lg">{g.title}</h2>
              <span className="ml-auto font-mono text-xs text-muted-foreground">{g.docs.length}</span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {g.docs.map((d) => <DocCard key={d.path} doc={d} />)}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
